import {
  Component,
  OnInit,
  AfterViewInit,
  Input,
  HostListener,
  EventEmitter,
  Output,
  ViewChild,
} from "@angular/core";
import { SafeUrl } from "@angular/platform-browser";
import { SignatureComponent } from "@progress/kendo-angular-inputs";
import { AF_CONSTANTS } from "../model/af.model";

@Component({
  selector: "app-wet-signature",
  templateUrl: "./wet-signature.component.html",
  styleUrls: ["./wet-signature.component.scss"],
})
export class WetSignatureComponent implements OnInit, AfterViewInit {
  // Signature
  value: string = "";
  readOnly: boolean = false;

  // Use theme colors
  color: string = "blue";
  strokeWidth: number = 3; // 1 normal 3 wide

  // Image
  imageURL?: SafeUrl;
  private rawImageURL?: string;

  @ViewChild("signature", { static: false }) signaturePad: SignatureComponent;

  @Input() signatureImg: string;
  @Output() sigURL: EventEmitter<string> = new EventEmitter();
  @Output() sigCheck: EventEmitter<boolean> = new EventEmitter();

  ngOnInit() {
    this.value = this.signatureImg; // Gets the base64 signature string from @Input
  }

  ngAfterViewInit() {
    this.signaturePad.valueChange.subscribe({
      next: (signature: string) => {
        if (AF_CONSTANTS.emptyCanvas === signature || !this.hasPixelsInCanvasDrawing()) { // Checks to see if the signature pad was only click on and no signature added by user
          this.signatureErrorCheck(true);
        } else {
          this.provideSignature(signature);
          this.signatureErrorCheck(false);
        }
      },
    });

    // ReadOnly if form is submitted
    let submitFlag = sessionStorage.getItem("submitFlag");
    if (submitFlag && submitFlag === "1") {
      this.readOnly = true;
    }

    // Signature pad is now available. Aria attributes not being read by JAWS, set through [attr.aria-label] for now
    let canvas = document.getElementsByTagName("canvas")[0];
    canvas.setAttribute(
      "aria-label",
      "* Signature pad press enter or use focus mode to sign with a mark, otherwise a mouse is required"
    );
    canvas.setAttribute("aria-required", "true");
    canvas.setAttribute("required", "");
    canvas.setAttribute("id", "sig_pad");
    document.getElementById("sig_pad").innerHTML =
      "<p>* Signature pad press enter or use focus mode to sign with a mark otherwise a mouse is required</p>"; //Fallback Text
  }

  /**
   * Emits a true or false value to help the parent component determine if the current signature pad is valid
   * True displays the error message
   * False removes the error message
   * @param value boolean
   */
  signatureErrorCheck(value: boolean): void {
    this.sigCheck.emit(value);
  }

  /**
   * Checks if the canvas itself contains any pixels that are not the transparent 255 RGB value.
   * Safari browsers provide a different data:image/png;base64,ABC...etc. image vs chrome and MSIE (Edge)
   * so this is needed to attempt capturing when Users are simply tapping or only clicking once without dragging the canvas to 
   * provide a signature
   * @returns boolean
   */
  hasPixelsInCanvasDrawing(): boolean {
    let canvas = document.getElementsByTagName("canvas")[0];
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;

    let hasPixels: boolean = false;
    for (let i = 0; i < pixels.length; i += 4) {
      // Check if any pixels are not the transparent 255, if they are not 255 then at least one pixel is in the canvas
      if (pixels[i] !== 255 || pixels[i + 1] !== 255 || pixels[i + 2] !== 255 || pixels[i + 3] !== 255) {
        hasPixels = true;
      }
    }

    return hasPixels;
  }

  /**
   * Emits a signature with the values string, null, or undefined based on the current contents of the signature pad
   * @param signature string
   */
  provideSignature(signature: string): void {
    if (!this.hasPixelsInCanvasDrawing()) {
      signature = null;
    }

    this.sigURL.emit(signature);
  }

  /**
   * Provides canvas after user provides input to the signature pad
   * @param canvas HTMLCanvasElement
   */
  drawComplete(canvas: HTMLCanvasElement): void {
    // Caputure if the user simply clicks on the canvas and provides an error. Kendo signature pad provides a default canvas image if signature pad is clicked
    if (AF_CONSTANTS.emptyCanvas === canvas.toDataURL() || !this.hasPixelsInCanvasDrawing()) {
      this.signatureErrorCheck(false);
    } else {
      this.provideSignature(canvas.toDataURL()); // Emits the signature pad data through @Output
      this.value = canvas.toDataURL(); // Gets the signature pad data and converts it to a base64 string
      this.signatureErrorCheck(true);
    }
  }

  /**
   * Removes error if user is currently focused on the signature pad
   */
  focus(): void {
    this.signatureErrorCheck(false);
  }

  /**
   * Adds error message if the user removes focus from the signature pad without providing a signature
   */
  blur(): void {
    if (this.value) {
      this.signatureErrorCheck(false);
    } else {
      this.signatureErrorCheck(true);
    }
  }

  /**
   * Provides keypress element on enter keypress
   * @param element 
   */
  @HostListener("window:keyup.enter", ["$event.target"]) // No longer works with Screen readers... need an alternative since NVDA and JAWS now consume enter key.
  target(element): void {
    if (element.id === "sig_pad" || element.id === "sign_canvas") {
      this.keyDown();
    }
  }

  /**
   * Provides an accessability option for users struggling to use a mouse.
   * Enter key provides an auto generated 'X' as the users signature
   */
  keyDown(): void {
    if (!this.value) {
      let canvas = document.getElementsByTagName("canvas")[0];
      let ctx = canvas.getContext("2d");
      ctx.font = "30px Arial";
      ctx.fillStyle = "blue";
      ctx.fillText("X", 30, 60);
      this.drawComplete(canvas);
    }
  }

  /**
   * Cleans up canvas to ensure image data is not carried across components
   */
  ngOnDestroy(): void {
    this.cleanUpImage();
  }

  /**
   * Clear signature button
   */
  onClear(): void {
    this.value = "";
    this.cleanUpImage();
    this.provideSignature(null);
    this.signatureErrorCheck(false);
  }

  /**
   * Clears image for ngOnDestroy()
   */
  private cleanUpImage(): void {
    if (this.rawImageURL) {
      URL.revokeObjectURL(this.rawImageURL);
      this.imageURL = undefined;
      this.rawImageURL = "";
    }
  }
}
